import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import type { BoardBaseArgs } from 'webapp/components/board/base';
import BoardBaseComponent from 'webapp/components/board/base';
import BoardBlocksMobileComponent from 'webapp/components/board/blocks/mobile';
import BoardMasterDateRangeSwitchComponent from 'webapp/components/board/master-date-range-switch';
import BoardMasterFilterComponent from 'webapp/components/board/master-filter';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataStatusIconComponent from 'webapp/components/data-status-icon';
import FetchLoaderComponent from 'webapp/components/fetch-loader';
import FormInputComponent from 'webapp/components/form/input';

import type CurrentUser from 'webapp/services/current-user';

interface BoardMobileArgs extends BoardBaseArgs {
	currentZoom?: number;
}

export default class BoardMobileComponent<T extends BoardMobileArgs> extends BoardBaseComponent<T> {
	@service declare currentUser: CurrentUser;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	@tracked isTitleTooLong = false;

	get currentZoom(): number {
		return this.args.currentZoom || 100;
	}

	get fontSize(): number {
		return this.currentZoom / 12;
	}

	get viewInnerStyle() {
		return htmlSafe(`font-size:${this.fontSize}px`);
	}

	get showStickyHeader() {
		return this.isViewOnly;
	}

	checkStickyHeader!: () => void;

	@action
	checkIfTitleTooLong(): void {
		schedule('afterRender', () => {
			const containerWidth = this.el.find('.scroll-offset').width() || 0,
				textWidth = this.el.find('.canEdit').outerWidth() || 0;
			this.isTitleTooLong = textWidth > containerWidth;
		});
	}

	@action
	scrollToTop(): void {
		this.el.find('.view-board-inner').scrollTop(0);
	}

	@action
	openEnableCustomDataUpdatePopup() {
		this.mmBoardsService.openEnableCustomDataUpdatePopup(this.model, 'mobile');
	}

	@action
	override setupView(node: HTMLElement): void {
		super.setupView(node);
		this.checkIfTitleTooLong();

		const stickyHeaderCt = this.el.find('.sticky-header');

		if (stickyHeaderCt.length) {
			const innerCt = this.el.find('.view-board-inner');

			const checkStickyHeader = () => {
				let scrolled = 0;

				if (innerCt.length) {
					scrolled = innerCt.scrollTop()! + innerCt.height()! + 100;
				}
				stickyHeaderCt.toggleClass('visible', scrolled >= (innerCt[0]?.scrollHeight || 0));
			};

			this.checkStickyHeader = checkStickyHeader;

			innerCt.on('scroll.stickyHeader', () => {
				debounce(checkStickyHeader, 100);
			});
		}
	}

	@action
	teardownView(): void {
		this.el.find('.view-board-inner').off('scroll.stickyHeader');
	}
	<template>
		<div
			class="view-board--mobile bgTheme-{{this.model.bgTheme}}
				colorTheme-{{this.model.themeId}}"
			style={{this.gradientStyle}}
			data-id={{this.model.id}}
			{{didInsert this.setupView}}
			{{didUpdate this.checkIfBoardLoaded this.model.id this.model.isDataLoading}}
			{{didUpdate this.checkIfTitleTooLong this.model.name}}
			{{willDestroy this.teardownView}}
		>

			<div
				class="view-board-inner {{if this.showBoardFetchLoader 'faded'}}"
				style={{this.viewInnerStyle}}
			>

				<div class="view-board-content">
					{{#if this.showLoader}}
						<div class="bar-container">
							<BarLoaderComponent @size="big" />
						</div>
					{{/if}}
					<div class="view-board-content-inner" data-id={{this.model.id}}>

						{{#if this.showStickyHeader}}
							<div class="sticky-header">
								<h1>{{this.model.name}}</h1>
								<a class="top" {{on "click" this.scrollToTop}}>
									<CommonUiIconComponent @name="ArrowUp" @size="sm" />
									Top
								</a>
							</div>
						{{/if}}

						<div class="scroll-offset">
							{{#if this.canEditName}}
								{{#if this.isEditingName}}
									<h1 class="editing">
										<FormInputComponent
											name="name"
											maxlength="65"
											@autofocus={{true}}
											@value={{this.editedName}}
											@focusOut={{this.endBoardNameEdit}}
											@loseFocusOnEnter={{true}}
										/>
									</h1>
								{{else}}
									<h1 class="canEdit {{if this.isTitleTooLong 'wholeWidth'}}">
										<label
											{{on "click" this.startBoardNameEdit}}
											class="editableTitle
												{{if this.isTitleTooLong 'longTitle'}}"
										>
											{{this.model.name}}
											<CommonUiIconComponent
												@name="Pencil"
												@size="dynamic"
												@class="edit-icon"
											/>
										</label>
									</h1>
								{{/if}}
							{{else}}
								<h1>
									{{this.model.name}}
								</h1>
							{{/if}}

							{{#if this.showBottomInfo}}
								<div class="bottomInfo bottomInfo--on-top">
									{{#if this.showFilterMasterSwitcher}}
										<BoardMasterFilterComponent
											@boardState={{@boardState}}
											@save={{this.changeMasterFilter}}
											@theme={{this.theme}}
											@isDisabled={{this.accountService.isDemoAccount}}
											@iconSize="dynamic"
										/>
									{{/if}}

									{{#if this.showDateRangeMasterSwitcher}}
										<BoardMasterDateRangeSwitchComponent
											@boardState={{@boardState}}
											@onChange={{this.masterDateRangeChanged}}
											@theme={{this.theme}}
											@isDisabled={{this.accountService.isDemoAccount}}
										/>
									{{/if}}

									{{#if this.showResetPreviewToOriginal}}
										<span class="default-view">
											<a {{on "click" this.resetPreviewToOriginal}}>
												<CommonUiIconComponent @name="Refresh" @size="sm" />
												reset
											</a>
										</span>
									{{/if}}
								</div>
							{{/if}}

						</div>

						{{#unless this.showLoader}}
							<BoardBlocksMobileComponent
								@boardState={{@boardState}}
								@isViewOnly={{this.isViewOnly}}
								@currentZoom={{this.currentZoom}}
								@skipBlockFetchLoaders={{this.showBoardFetchLoader}}
								@skipActions={{@skipActions}}
							/>
						{{/unless}}

						<div class="bottomInfo">

							{{#unless this.showLoader}}

								{{#if this.showBottomInfo}}
									{{#unless this.hideBranding}}
										{{#if @skipActions}}
											<a><span class="powered">Powered by</span></a>
										{{else}}
											<a href="https://databox.com" target="_blank">
												<span class="powered">Powered by</span>
											</a>
										{{/if}}
									{{/unless}}
								{{/if}}

								{{#if this.isSampleData}}
									<div class="warning">
										<div class="sample">Note: Showing sample data</div>
									</div>
								{{else}}
									{{#if this.isExpiredSpace}}
										<div class="warning">
											<div class="expired">
												Account has expired. Data refresh is on hold.
												{{#if this.showHelpArticles}}
													<a
														href="https://help.databox.com/data-sync-storage-security-in-databox"
														target="_blank"
													>
														Refresh my data
													</a>
												{{/if}}
											</div>
										</div>
									{{else if this.isInactiveSpace}}
										<div class="warning">
											<div class="expired">
												Data refresh is on hold due to account inactivity.
												{{#if this.showHelpArticles}}
													<a
														href="https://help.databox.com/data-sync-storage-security-in-databox"
														target="_blank"
													>
														Refresh my data
													</a>
												{{/if}}
											</div>
										</div>
									{{else}}
										{{#if this.showLoadingInsteadOfLastSync}}
											<span class="sync loading">
												Loading...
											</span>
										{{else if this.model.lastSyncText}}
											<span class="sync">
												last update
												{{this.lastSyncText}}
												<DataStatusIconComponent
													@context="board"
													@lastSync={{this.model.lastSync}}
													@theme={{this.model.bgTheme}}
													@skipActions={{@skipActions}}
													@openEnableCustomDataUpdatePopup={{this.openEnableCustomDataUpdatePopup}}
												/>
											</span>
										{{/if}}
									{{/if}}
								{{/if}}

							{{/unless}}
						</div>

					</div>
				</div>

			</div>

			{{#if this.showBoardFetchLoader}}
				<FetchLoaderComponent @colors={{this.model.themeMainColorPair}} @type="board" />
			{{/if}}

		</div>
	</template>
}
